import { Link, useParams } from "react-router-dom";

import { docCode, sectionCode, useNav } from "./api";
import styles from "./manual.module.css";
import { buildPageTree } from "./navTree";
import { NewPageButton } from "./NewPageButton";

import { Breadcrumbs } from "@/components/Breadcrumbs";
import { PageHeader } from "@/components/PageHeader";
import { useCurrentUser } from "@/hooks/useCurrentUser";

/** Section landing: the top-level pages in one manual section (prototype
 * design). A parent page's card says how many pages it holds. */
export function SectionPage() {
  const { sectionSlug } = useParams();
  const { sections } = useNav();
  const { permissions } = useCurrentUser();
  const canEdit = permissions.includes("cms.edit");

  if (!sections) return <div className={styles.view + " " + styles.quiet}>Loading…</div>;

  const section = sections.find((s) => s.slug === sectionSlug);
  if (!section) {
    return (
      <div className={styles.view}>
        <div className={styles.emptyState}>
          <strong>Section not found.</strong>
          <br />
          The section doesn&rsquo;t exist, or isn&rsquo;t available to you. If you think you should
          have access, ask your manager.
        </div>
      </div>
    );
  }

  const pages = buildPageTree(canEdit ? section.pages : section.pages.filter((p) => p.published));

  return (
    <div className={styles.view}>
      <Breadcrumbs items={[{ label: "OpsHub", to: "/" }, { label: section.title }]} />
      <PageHeader
        title={
          <>
            <span className={styles.sectionHeadingCode}>{sectionCode(section.ordinal)}.</span>{" "}
            {section.title}
          </>
        }
      />
      <div className={styles.sectionCards}>
        {pages.map(({ page, children }) => {
          // Count what the parent page lists: its published children only.
          const childCount = children.filter((c) => c.published).length;
          return page.published ? (
            <Link key={page.id} to={`/manual/${section.slug}/${page.slug}`} className={styles.card}>
              <div className={styles.cardTitle}>
                <span className={styles.cardCode}>{docCode(section.ordinal, page.ordinal)}</span>
                {page.title}
              </div>
              {page.snippet && <div className={styles.cardSummary}>{page.snippet}</div>}
              {childCount > 0 && (
                <div className={styles.cardMeta}>
                  {childCount} page{childCount === 1 ? "" : "s"}
                </div>
              )}
              <div className={styles.cardRead}>Read page</div>
            </Link>
          ) : (
            <Link
              key={page.id}
              to={`/edit/${page.id}`}
              className={`${styles.card} ${styles.cardDraft}`}
            >
              <div className={styles.cardTitle}>
                <span className={styles.cardCode}>{docCode(section.ordinal, page.ordinal)}</span>
                {page.title}
              </div>
              <div className={styles.cardSummary}>
                Draft — not yet published. Open to keep editing.
              </div>
            </Link>
          );
        })}
        {pages.length === 0 && (
          <div className={styles.emptyState}>
            Nothing in this section yet — content arrives with the section-by-section build.
          </div>
        )}
      </div>
      {canEdit && (
        <NewPageButton
          sectionId={section.id}
          sectionSlug={section.slug}
          parents={pages.map(({ page }) => ({ id: page.id, title: page.title }))}
        />
      )}
    </div>
  );
}
